import { basename } from '@/lib/desktop-paths';
import type {
  DesktopShellState,
  SyncedThread,
  SyncedWorkspace,
  SyncedWorktree,
} from '@/lib/desktop-shell/store';
import type { ConversationWorktree } from '@/lib/local-conversations';
import type { ConversationPullRequestClaim } from '@/lib/pull-request';

/*
 * Which worktree is a conversation's, and which repository a folder is a worktree of, come from
 * the server's records only (`workspace.source_workspace_id`, `thread.worktree_workspace_id`):
 * every device, window and reload agrees, and a worktree cleaned up is still known.
 */

type Records = {
  syncedThreads: readonly Pick<SyncedThread, 'id' | 'worktree'>[];
  syncedWorkspaces: readonly SyncedWorkspace[];
};

type ShownState = Records & Pick<DesktopShellState, 'chatId' | 'workspace'>;

/**
 * What a folder is called wherever the shell names its repository: the server's name for its
 * workspace (a worktree's is its repository's), the folder's own name only when the server does
 * not know it.
 */
export function workspaceName(
  workspaces: readonly Pick<SyncedWorkspace, 'rootPath' | 'name'>[],
  root: string,
): string {
  return workspaces.find((entry) => entry.rootPath === root)?.name || basename(root);
}

/** A local conversation's worktree, as the sidebar and shell keep it. */
export function syncedWorktree(
  worktree: ConversationWorktree | null | undefined,
): SyncedWorktree | null {
  if (worktree === null || worktree === undefined) return null;
  return {
    path: worktree.path,
    sourceRoot: worktree.sourceRoot,
    ...(worktree.available === undefined ? {} : { available: worktree.available }),
  };
}

/** The server's record of a folder of this machine. */
export function localWorkspaceRecord(
  workspaces: readonly SyncedWorkspace[],
  root: string | null,
): SyncedWorkspace | undefined {
  if (root === null) return undefined;
  return workspaces.find(
    (entry) => entry.rootPath === root && entry.runtime !== 'cloud' && entry.onCloud !== true,
  );
}

/**
 * A conversation's own worktree: the folder it works in when that is a worktree, else the one the
 * server keeps for it while it continues in the main checkout; null when it has none.
 */
export function conversationWorktree(
  records: Records,
  chatId: string,
  workspace: string | null,
): SyncedWorktree | null {
  const record = localWorkspaceRecord(records.syncedWorkspaces, workspace);
  if (record?.sourceRoot)
    return { path: record.rootPath, sourceRoot: record.sourceRoot, available: true };
  return records.syncedThreads.find((entry) => entry.id === chatId)?.worktree ?? null;
}

/** The shown conversation's own worktree (`conversationWorktree`). */
export function shownWorktree(state: ShownState): SyncedWorktree | null {
  return conversationWorktree(state, state.chatId, state.workspace);
}

/** The worktree path of the shown conversation, where it works or which it left; null: none. */
export function selectAssociatedWorktreePath(state: ShownState): string | null {
  return shownWorktree(state)?.path ?? null;
}

/**
 * The repository checkout of the shown conversation's folder when that folder is a worktree;
 * null in the checkout itself or outside Git.
 */
export function selectWorktreeSourceRoot(state: ShownState): string | null {
  const worktree = shownWorktree(state);
  return worktree !== null && worktree.path === state.workspace ? worktree.sourceRoot : null;
}

/**
 * In a folder other conversations share (the main checkout), the pull request the server records
 * the shown conversation's work is on, null when none; undefined in a worktree of its own, whose
 * branch's pull request is its own.
 */
export function selectSharedFolderPullRequest(
  state: Omit<ShownState, 'syncedThreads'> & {
    syncedThreads: readonly Pick<SyncedThread, 'id' | 'worktree' | 'pullRequest'>[];
  },
): ConversationPullRequestClaim | null | undefined {
  if (selectWorktreeSourceRoot(state) !== null) return undefined;
  return state.syncedThreads.find((entry) => entry.id === state.chatId)?.pullRequest ?? null;
}
